import { INotification, INotificationFilter, NotificationStatus, NotificationType } from "types";

interface IFindLatestNotificationParams {
  status: NotificationStatus
}
export const findLatestNotification = (notifications: INotification[], params:  IFindLatestNotificationParams| null = null): INotification | null => {
    if (notifications.length === 0) {
      return null;
    }
  
    let latestNotification: INotification = notifications[0];
  
    for (let i = 1; i < notifications.length; i++) {
      if (params && notifications[i].status !== params.status) {
        continue
      }
      const currentNotification = notifications[i];
      const currentCreatedAt = new Date(currentNotification.created_at);
      const latestCreatedAt = new Date(latestNotification.created_at);
  
      if (currentCreatedAt > latestCreatedAt) {
        latestNotification = currentNotification;
      }
    }
  
    return latestNotification;
  };

  /**
 * Merge a freshly fetched page into the notifications already held.
 *
 * Pages used to be concatenated as-is. Two fetches firing before the cursor state
 * settled (the infinite-scroll observer can do it, and React's double-invoked effects
 * in development always do) appended the same page twice: the list showed duplicates
 * until a refresh, and the surviving copy could be the OLDER object — which is how an
 * archived notification kept appearing under Unread.
 *
 * Keyed by id, newest `updated_at` wins, order preserved.
 *
 * "Last one in wins" is not good enough: a page request that captured a notification
 * as unread can land AFTER the user archived it, putting the stale unread copy back
 * into state — the very symptom this is meant to fix.
 */
export const mergeNotifications = (
  existing: INotification[],
  incoming: INotification[]
): INotification[] => {
  const byId = new Map<string, INotification>()
  for (const notification of [...existing, ...incoming]) {
    const held = byId.get(notification.id)
    if (held && !isNewer(notification, held)) continue
    byId.set(notification.id, notification)
  }
  return Array.from(byId.values())
}

const isNewer = (candidate: INotification, held: INotification): boolean => {
  const candidateAt = Date.parse(candidate.updated_at ?? '')
  const heldAt = Date.parse(held.updated_at ?? '')
  // Without usable timestamps, keep what we already have: state has been through the
  // local updates (archive, read, pin), a late response has not.
  if (isNaN(candidateAt) || isNaN(heldAt)) return false
  return candidateAt > heldAt
}

export const timeElapsedSinceReceived = (receivedTimeStr) => {
    const receivedTime = new Date(receivedTimeStr);
    const currentTime = new Date();

    // the time difference in millis
    // @ts-ignore
    const timeDifference = currentTime - receivedTime;

    const seconds = Math.floor(timeDifference / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days >= 365) {
        // `${days} years ago` printed the DAY count in the years slot: a notification
        // from two years back read "760 years ago".
        // Floored, so "more than" is the honest word at every count.
        const years = Math.floor(days / 365);
        return (years === 1) ? `more than 1 year ago` : `more than ${years} years ago`;
    } else if (days > 0 && days < 365) { 
        return (days === 1) ? `${days} day ago` : `${days} days ago`;
    } else if (hours > 0) {
        return (hours === 1) ? `${hours} hour ago` : `${hours} hours ago`;
    } else if (minutes > 0) {
        return (minutes === 1) ? `${minutes} minute ago` : `${minutes} minutes ago`;
    } else {
        return 'Just now';
    }
}

export const beautifyNotificationDate = (dateString: string) => {
  const options = {  weekday: 'short', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false  };
  // @ts-ignore
  const prnDt = new Date(dateString).toLocaleTimeString('en-us', options);
  return prnDt 
}
export const placeNotificationTypeIcon = (notificationType: NotificationType) => {
  switch (notificationType) {
    case NotificationType.Error:
      return "error"
    case NotificationType.Info:
      return "info"
    case NotificationType.Success:
      return "check_circle"
    case NotificationType.Warning:
      return "warning"
      case NotificationType.Persistent:
        return "info"
  }
}